// 47284: the ONE freshness verdict shown next to "updated <time>" wherever a page offers Refresh data (RefreshDataButton renders it).
// Same verdict as Data Refreshes (server lib/refreshVerdict.js, GET /api/refresh-jobs/verdicts, shared 30 s cache): red chip = a job errored,
// amber "Stale" = overdue / failed parent / dependency warning (tooltip says why + next run, and "scheduler off" when it is), quiet grey hint
// = a parent changed since (info), nothing when fresh. Chip classes from shell.css (.chip.warn / .chip.crit), like the Data Refreshes page.
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const TTL = 30000;
let cache = null, at = 0, inflight = null;
const subs = new Set();
export function loadRefreshVerdicts(force = false) {
  if (inflight) return inflight;
  if (!force && cache && Date.now() - at < TTL) return Promise.resolve(cache);
  inflight = api.request('/refresh-jobs/verdicts').then((r) => { cache = r; at = Date.now(); subs.forEach((f) => f(r)); return r; }, () => cache).finally(() => { inflight = null; });
  return inflight;
}
const hhmm = (iso) => { const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

// One verdict item in the viewer's language (t = useTranslation('dataRefreshes')); the server's English text is the fallback.
export function verdictText(t, it) {
  return t(`verdict.${it.code}`, { defaultValue: it.text, parent: it.parent_label, child: it.child_label, at: it.at ? hhmm(it.at) : '', next: it.next_run, trigger: t(`trig.${it.trigger}`, { defaultValue: it.trigger || '' }) })
    + (it.scheduler_off && it.code === 'overdue' ? ` (${t('schedulerOff')})` : '');
}

// anchor = one job_key or a comma list; the worst level of those jobs wins, all their items are listed.
// refresh = also offer a "Refresh now" link (stale / error only) that runs the anchor's closure through the same cascade as Refresh data.
export default function RefreshVerdictChip({ anchor, refresh = false }) {
  const { t } = useTranslation('dataRefreshes');
  const [data, setData] = useState(cache);
  const [busy, setBusy] = useState(false);
  useEffect(() => { subs.add(setData); loadRefreshVerdicts(); return () => { subs.delete(setData); }; }, [anchor]);
  const keys = String(anchor || '').split(',').map((k) => k.trim()).filter(Boolean);
  const vs = keys.map((k) => data && data.verdicts && data.verdicts[k]).filter(Boolean);
  const items = vs.flatMap((v) => v.items.map((i) => ({ ...i, job: v.label })));
  const level = vs.some((v) => v.level === 'error') ? 'error' : vs.some((v) => v.level === 'warn') ? 'warn' : vs.some((v) => v.level === 'info') ? 'info' : 'ok';
  if (level === 'ok') return null;
  const next = vs.map((v) => v.next_run).filter(Boolean)[0];
  const tip = [...items.filter((i) => i.level !== 'info' || level === 'info').map((i) => `${keys.length > 1 ? `${i.job}: ` : ''}${verdictText(t, i)}`), next && level !== 'info' ? `${t('nextRun')} ${next}` : null].filter(Boolean).join('\n');
  const refreshNow = async () => {
    setBusy(true);
    try {
      const { run_id } = await api.refreshCascadeRun(keys.join(','));
      for (let i = 0; i < 150; i += 1) { await new Promise((r) => setTimeout(r, 2000)); const st = await api.refreshCascadeStatus(run_id); if (st.done) break; }
    } catch (e) { /* the chip stays; the verdict re-read below tells the truth */ }
    await loadRefreshVerdicts(true); setBusy(false);
  };
  const act = refresh && level !== 'info' && level !== 'ok'
    ? <button type="button" className="btn sm" data-testid="refresh-verdict-now" disabled={busy} onClick={refreshNow} style={{ marginLeft: 6 }}>{busy ? t('running') : t('refreshNow')}</button> : null;
  if (level === 'info') return <span data-testid="refresh-verdict-info" title={tip} style={{ fontSize: 11.5, color: 'var(--muted, #94a3b8)', maxWidth: 360, cursor: 'help' }}>{verdictText(t, items.find((i) => i.level === 'info'))}</span>;
  return <span style={{ display: 'inline-flex', alignItems: 'center' }}><span data-testid={`refresh-verdict-${level}`} className={`chip ${level === 'error' ? 'crit' : 'warn'}`} title={tip} style={{ cursor: 'help' }}>{t(`level.${level}`)}</span>{act}</span>;
}
